import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon, RefreshCwIcon, DatabaseIcon, GaugeIcon, DownloadCloudIcon, ActivityIcon, BugIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WordPressOnderhoudPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'WordPress onderhoud | Veilig & up-to-date | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'WordPress onderhoud: houd je WordPress website veilig, snel en up-to-date. Professioneel onderhoud door Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'WordPress onderhoud | Veilig & up-to-date | Noord',
      'og:description': 'WordPress onderhoud: houd je WordPress website veilig, snel en up-to-date. Professioneel onderhoud door Noord Digital.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/wordpress-onderhoud',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['pakketten', 'contact'];
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            const anchor = document.getElementById('anchor');
            if (anchor) {
              anchor.href = `#${sectionId}`;
            }
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe vaak worden WordPress updates uitgevoerd?',
    a: "Wij voeren wekelijks controles uit op beschikbare updates voor WordPress core, plugins en thema's. Kritieke beveiligingsupdates worden direct doorgevoerd. Reguliere updates plannen wij zorgvuldig in om compatibiliteitsproblemen te voorkomen."
  }, {
    q: 'Wat gebeurt er als er een probleem optreedt?',
    a: 'Bij problemen grijpen wij direct in. Dankzij onze dagelijkse backups kunnen we jouw website snel herstellen naar een werkende versie. Daarnaast monitoren wij je website 24/7, zodat we problemen vaak al oplossen voordat jij ze merkt.'
  }, {
    q: 'Heb ik WordPress onderhoud echt nodig?',
    a: 'Ja. WordPress is open-source software die regelmatig updates ontvangt voor beveiliging en functionaliteit. Zonder onderhoud loop je risico op beveiligingslekken, trage laadtijden en compatibiliteitsproblemen met plugins. Professioneel WordPress onderhoud voorkomt deze problemen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2915" data-mp="2g9k07g78k7le">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2916" data-mp="2l9y9t2ohjplj">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2917" data-mp="2l7vbzwh3cs69">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2918" data-mp="2q9atjek28gjw" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2919" data-mp="2cjze9z1r65ea" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2920" data-mp="2mbvmi2xjzbw8" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2921" data-mp="1tp15xh3uwwo1">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2922" data-mp="23l48go2mexbi">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2923" data-mp="1ymkkki4gvbgi">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2924" data-mp="3eypxm98hcyx1">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2925" data-mp="3a297j9hq0ahb">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2926" data-mp="3mjsd9gnotftk">/</span>
            <span className="text-accent-light font-medium" data-id="element-2927" data-mp="3mln050iiri0a">
              WordPress onderhoud
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2928" data-mp="38mn9xofcyv20">
            WordPress onderhoud zonder zorgen
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2929" data-mp="46m53v5pw5uj">
            Wij houden je WordPress website veilig, snel en up-to-date. Zodat
            jij je kunt focussen op ondernemen.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2930" data-mp="3vki6qdph6u44">
            <a href="#pakketten" onClick={e => scrollToSection(e, 'pakketten')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2931" data-mp="3vixc6o35vb69">
              Bekijk onderhoudspakketten
              <ArrowRightIcon className="w-5 h-5" data-id="element-2932" data-mp="xr3hehe4stbf" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom onderhoud nodig is */}
      <section className="py-24 bg-surface-muted" data-id="element-2933" data-mp="sss9wdbb6uwd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2934" data-mp="nqt652bnpvyw">
          <div className="text-center mb-16" data-id="element-2935" data-mp="isj90m6dglan">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2936" data-mp="iucg22kwd7j6">
              Waarom WordPress onderhoud nodig is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2937" data-mp="a07u9xkmemai">
              Een WordPress website zonder onderhoud is kwetsbaar. Voorkom
              problemen met een professionele WordPress onderhoud service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2938" data-mp="1s2rtholb0h">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2939" data-mp="3r9mff2xejyn9">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2940" data-mp="3h3uu85atx1q8">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2941" data-mp="3m264gh51de8z" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2942" data-mp="3r3vfvxw67txl">
                Security updates
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2943" data-mp="3w15gm9p0fs0q">
                WordPress beveiligingsupdates beschermen je website tegen
                hackers en malware. Zonder tijdige updates loop je onnodig
                risico.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2944" data-mp="38bb4xvgfg9i2">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2945" data-mp="3d446kagln1px">
                <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-2946" data-mp="2fmvs9fkrq39w" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2947" data-mp="2klq94bz6dhrd">
                Plugin updates
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2948" data-mp="2ocf4kol6olm">
                Verouderde plugins veroorzaken conflicten en beveiligingslekken.
                Wij houden al je plugins actueel en compatibel.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2949" data-mp="7mx41nye1lur">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2950" data-mp="lmgckhv42z7l">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-2951" data-mp="hlmo9ndrbie6" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2952" data-mp="vchzh6bqabkx">Backups</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2953" data-mp="vem11hm5myi1">
                Dagelijkse backups zorgen ervoor dat je website altijd te
                herstellen is, mocht er onverhoopt iets misgaan.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2954" data-mp="10c4e9k77fzvu">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2955" data-mp="1jwuqzqc7bi5s">
                <GaugeIcon className="w-6 h-6 text-accent" data-id="element-2956" data-mp="1nz9e4mmwl42v" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2957" data-mp="19zgn0q7gslud">
                Performance
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2958" data-mp="1exi6vdcpsp6w">
                Een snelle website scoort beter in Google en zorgt voor een
                betere gebruikerservaring. Wij optimaliseren continu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij doen */}
      <section id="pakketten" className="py-24 bg-white" data-id="element-2959" data-mp="r9s1fnpi6hro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2960" data-mp="w7tjq6f3ud23">
          <div className="text-center mb-16" data-id="element-2961" data-mp="w81t3oc0r08j">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2962" data-mp="ha13td5nbmnd">
              Wat wij doen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2963" data-mp="m8kqjfs2d2gs">
              Ons WordPress onderhoud pakket dekt alles wat je nodig hebt om
              zorgeloos online te zijn.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2964" data-mp="1nrct4s313thd">
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2965" data-mp="18tapyizwta0n">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2966" data-mp="1dr2hvd0k0w4u">
                <RefreshCwIcon className="w-6 h-6 text-navy-700" data-id="element-2967" data-mp="21i7tffzgp29c" />
              </div>
              <div data-id="element-2968" data-mp="26gsybspnsa44">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2969" data-mp="26ey9i3b9nxur">
                  WordPress updates
                </h3>
                <p className="text-slate-600" data-id="element-2970" data-mp="1rr3icrcwq1js">
                  Wekelijkse controle en uitvoering van WordPress core, plugin
                  en thema updates. Altijd getest op compatibiliteit voordat we
                  live gaan.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2971" data-mp="2p8269xjs6qtl">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2972" data-mp="dq7e1h6jwdhj">
                <DownloadCloudIcon className="w-6 h-6 text-navy-700" data-id="element-2973" data-mp="8sfcrwk0nf0o" />
              </div>
              <div data-id="element-2974" data-mp="ngs8qtj3sxv6">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2975" data-mp="njegat5zvdde">
                  Backups
                </h3>
                <p className="text-slate-600" data-id="element-2976" data-mp="3qy2254n3uria">
                  Dagelijkse automatische backups van je volledige website en
                  database. Extern opgeslagen zodat je data altijd veilig is.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2977" data-mp="3uxauq4f2026">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2978" data-mp="3vvtuu0vkqhnj">
                <ActivityIcon className="w-6 h-6 text-navy-700" data-id="element-2979" data-mp="1f6y68nvscmqw" />
              </div>
              <div data-id="element-2980" data-mp="1b6miv3c1g8bq">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2981" data-mp="1b98qf2yxinty">
                  Monitoring
                </h3>
                <p className="text-slate-600" data-id="element-2982" data-mp="cvcxx9az6oja">
                  24/7 uptime monitoring van je website. Bij downtime ontvangen
                  wij direct een melding en grijpen we in.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2983" data-mp="luqaiauebt88">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2984" data-mp="3lc7qnvy26sjn">
                <BugIcon className="w-6 h-6 text-navy-700" data-id="element-2985" data-mp="3qb13rn9fohja" />
              </div>
              <div data-id="element-2986" data-mp="3ubl5z58v6bca">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2987" data-mp="3uaa08zvuytij">
                  Technische support
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2988" data-mp="2z06yj6teos8">
                  Loop je ergens tegenaan? Wij helpen je snel weer op weg. Van
                  formulieren tot weergaveproblemen. Wij staan paraat.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-2989" data-mp="2z1y8txot2zyg">
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg group" data-id="element-2990" data-mp="2u34xobwzrl1g">
              Vraag een onderhoudspakket aan
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" data-id="element-2991" data-mp="2bd5cw186os3u" />
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-2992" data-mp="22cqnapo6squj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2993" data-mp="22annxf159ld5">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2994" data-mp="1xcufsgga6ha8">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2995" data-mp="1sf2pfqvab385">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2996" data-mp="3shljymkhwkpe">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2997" data-mp="3iq7lmi4a5qxu">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2998" data-mp="3np28bv5dbzn8">
              Website hosting
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2999" data-mp="3dsfon9os3ems">•</span>
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3000" data-mp="2zseo5vcfe97u">
              Website laten maken
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-3001" data-mp="12zcqrada5zqu">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3002" data-mp="12z2zljgi1mjk">
          <div className="text-center mb-16" data-id="element-3003" data-mp="2nh4069ja47pl">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3004" data-mp="2nhx0osrna0za">
              Veelgestelde vragen over WordPress onderhoud
            </h2>
          </div>

          <div className="space-y-4" data-id="element-3005" data-mp="2x8i6b05qk5rp">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-3006" data-mp="2sggoyztpnout" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-3007" data-mp="369y5scv0inh5">
                  <span className="font-bold text-navy-900" data-id="element-3008" data-mp="3b784yk87edi6">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-3009" data-mp="1lrrwz860mxtv" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-3010" data-mp="1gtyt4ls6qfwr" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-3011" data-mp="2aixwydrohd3y">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-3012" data-mp="2khwvv4mn5udr" />
    </main>;
}